import * as React from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { useForgotPassword } from '@/features/auth/api';
import { useToast } from '@/shared/hooks/useToast';

export const Route = createFileRoute('/forgot-password')({
  component: ForgotPasswordPage,
});

const forgotPasswordSchema = z.object({
  email: z.string().email('Invalid email address'),
});

function ForgotPasswordPage(): React.JSX.Element {
  const [email, setEmail] = React.useState('');
  const [error, setError] = React.useState<string | undefined>();
  const [success, setSuccess] = React.useState(false);
  const forgotPasswordMutation = useForgotPassword();
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(undefined);
    setSuccess(false);

    const result = forgotPasswordSchema.safeParse({ email });

    if (!result.success) {
      setError(result.error.issues[0]?.message || 'Invalid email');
      return;
    }

    try {
      await forgotPasswordMutation.mutateAsync({ email });
      setSuccess(true);
      toast({
        title: 'Success',
        description: 'Password reset instructions have been sent to your email.',
      });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to send reset email';
      setError(errorMessage);
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Forgot Password</CardTitle>
          <CardDescription>
            Enter your email address and we'll send you instructions to reset your password
          </CardDescription>
        </CardHeader>
        <form onSubmit={handleSubmit}>
          <CardContent className="space-y-4">
            {success && (
              <div className="p-4 bg-green-50 border border-green-200 rounded-md">
                <p className="text-sm text-green-800">
                  Check your email for password reset instructions. If you don't see it, check your spam folder.
                </p>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setError(undefined);
                }}
                required
                autoComplete="email"
                disabled={success}
              />
              {error && (
                <p className="text-sm text-destructive">{error}</p>
              )}
            </div>
          </CardContent>
          <CardFooter className="flex flex-col space-y-4">
            <Button
              type="submit"
              className="w-full"
              disabled={forgotPasswordMutation.isPending || success}
            >
              {forgotPasswordMutation.isPending ? 'Sending...' : 'Send Reset Instructions'}
            </Button>
            <div className="flex items-center justify-center gap-4 text-sm text-muted-foreground">
              <Link to="/login" className="text-primary hover:underline">
                Back to Sign In
              </Link>
              <span>•</span>
              <Link to="/register" className="text-primary hover:underline">
                Create Account
              </Link>
            </div>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
}
